/**
 * app/layouts/LayoutMain.tsx
 * Shell principal del POS: topbar + navegación responsive (sidebar en desktop/
 * tablet, bottom nav + "Más" en móvil) + content area.
 *
 * UX-SHELL: solo cambia la PRESENTACIÓN de la navegación. Mantiene SECTIONS,
 * rutas (`to`), gating por permiso (isVisible) y el selector de contexto tal cual.
 */

import { useEffect, useState } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import {
  Home,
  Globe,
  ShoppingCart,
  Banknote,
  Package,
  Tag,
  ArrowLeftRight,
  Users,
  Wrench,
  ShieldCheck,
  Receipt,
  Truck,
  CreditCard,
  PackageCheck,
  BarChart3,
  History,
  Building2,
  KeyRound,
  Settings,
  ClipboardList,
  MoreHorizontal,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useOnlineStatus } from '@shared/hooks/useOnlineStatus'
import { useAuth } from '@features/auth/hooks/useAuth'
import { useActiveStoreConfigSync } from '@features/auth/hooks/useActiveStoreConfigSync'
import { useMarcaTicketSync } from '@shared/hooks/useMarcaTicketSync'
import { countPendingForGuard } from '@lib/offline/offlineSaleIntent'
import { OfflinePendingGuardModal } from '@features/pos/components/OfflinePendingGuardModal'
import { usePermissions } from '@shared/hooks/usePermissions'
import { useTransferVerifyAccess } from '@features/finance/hooks/useTransferVerifications'
import { useWarrantyPermissions } from '@features/warranties/hooks/useWarrantyPermissions'
import { ActiveContextSelector } from '@features/auth/components/ActiveContextSelector'
import { NotificationBell } from '@features/notifications/components/NotificationBell'
import { cn } from '@shared/utils/cn'
import { resolveBrandTheme } from '@shared/utils/brandTheme'
import { getStoreConfig, type StoreConfig } from '@features/organization/utils/storeConfig'
import { canAccessRepairs, type RuleResult } from '@features/organization/utils/storeRules'
import { isCorporateScope } from '@features/organization/utils/accessControl'
import type { Permission } from '@shared/types/roles.types'

// ── Menú de navegación ─────────────────────────────────────────────────────
// Solo REAGRUPA y RENOMBRA. Mantiene rutas (`to`) y permisos legacy actuales.
// Cada item se muestra si el usuario tiene su permiso legacy (mismo gating que
// hoy). Una sección se oculta si no tiene ningún item visible.

// permission opcional: sin permiso = siempre visible; array = basta cualquiera (OR).
// storeGate opcional: además del permiso, oculta el item si la config de la
// tienda activa lo apaga (p.ej. Taller con repairs.enabled=false).
type NavItem = {
  to: string
  icon: string
  label: string
  permission?: Permission | Permission[]
  storeGate?: (cfg: StoreConfig) => RuleResult
}
type NavSection = { key: string; label: string; items: NavItem[] }

const SECTIONS: NavSection[] = [
  {
    key: 'principal',
    label: 'Principal',
    // Inicio: siempre visible para cualquier usuario autenticado (sin permiso).
    items: [{ to: '/dashboard', icon: '🏠', label: 'Inicio' }],
  },
  {
    key: 'operacion',
    label: 'Operación',
    items: [
      { to: '/pos', icon: '🛒', label: 'Vender', permission: 'vender' },
      // Caja visible con permiso de operar caja O de solo ver caja.
      {
        to: '/cash-movements',
        icon: '💵',
        label: 'Caja',
        permission: ['movimientos_caja', 'ver_caja'],
      },
      { to: '/inventory', icon: '📦', label: 'Inventario', permission: 'ver_inventario' },
      { to: '/products', icon: '🏷️', label: 'Artículos', permission: 'ver_inventario' },
      { to: '/my-work', icon: '✅', label: 'Mi trabajo', permission: 'transferencias' },
      // Centro de tareas (motor transversal): sin permiso = siempre visible; el RLS acota el contenido.
      { to: '/tasks', icon: '🗂️', label: 'Tareas' },
      // Aprobaciones de descuento (00408): visible para todos; el servidor sólo muestra lo que cada quien puede aprobar.
      { to: '/aprobaciones', icon: '✋', label: 'Aprobaciones' },
      { to: '/transfers', icon: '🔀', label: 'Traspasos', permission: 'transferencias' },
      { to: '/clients', icon: '👥', label: 'Clientes', permission: 'gestion_clientes' },
      {
        to: '/repairs',
        icon: '🔧',
        label: 'Reparaciones',
        permission: 'gestionar_reparaciones',
        storeGate: canAccessRepairs,
      },
      // 00327: Garantías se gatea por permiso fino (warranties.view) en isVisible, no por legacy.
      { to: '/garantias', icon: '🛡️', label: 'Garantías' },
    ],
  },
  {
    key: 'administracion',
    label: 'Administración',
    items: [
      { to: '/procurement', icon: '📦', label: 'Abastecimiento', permission: 'compras' },
      { to: '/purchases', icon: '🧾', label: 'Compras', permission: 'compras' },
      { to: '/suppliers', icon: '🚚', label: 'Proveedores', permission: 'compras' },
      { to: '/payables', icon: '💳', label: 'Cuentas por pagar', permission: 'compras' },
      { to: '/reports', icon: '📊', label: 'Reportes', permission: 'ver_reportes_basicos' },
      { to: '/sales', icon: '🧾', label: 'Ventas', permission: 'ver_reportes_basicos' },
      { to: '/history', icon: '📚', label: 'Historial', permission: 'ver_historial_propio' },
    ],
  },
  {
    key: 'empresa',
    label: 'Empresa',
    items: [
      { to: '/organization', icon: '🏢', label: 'Empresa y Tiendas', permission: 'configuracion' },
      { to: '/users', icon: '🔑', label: 'Usuarios y Permisos', permission: 'gestion_usuarios' },
    ],
  },
  {
    key: 'sistema',
    label: 'Sistema',
    // Configuración tiene su propia pantalla (/settings), separada de Empresa.
    items: [{ to: '/settings', icon: '⚙️', label: 'Configuración', permission: 'configuracion' }],
  },
]

// Icono lucide por ruta (presentación; reemplaza los emojis del nav).
const NAV_ICON: Record<string, LucideIcon> = {
  '/dashboard': Home,
  '/corporate': Globe,
  '/pos': ShoppingCart,
  '/cash-movements': Banknote,
  '/inventory': Package,
  '/products': Tag,
  '/transfers': ArrowLeftRight,
  '/tasks': ClipboardList,
  '/clients': Users,
  '/repairs': Wrench,
  '/garantias': ShieldCheck,
  '/procurement': PackageCheck,
  '/purchases': Receipt,
  '/suppliers': Truck,
  '/payables': CreditCard,
  '/reports': BarChart3,
  '/sales': Receipt,
  '/history': History,
  '/organization': Building2,
  '/users': KeyRound,
  '/settings': Settings,
}

// Rutas primarias del bottom nav móvil (en orden de prioridad). El resto va a "Más".
const MOBILE_PRIMARY = ['/dashboard', '/pos', '/inventory', '/transfers']

function NavIcon({ to, className }: { to: string; className?: string }) {
  const Icon = NAV_ICON[to] ?? Package
  return <Icon className={className} />
}

// ── Shell ─────────────────────────────────────────────────────────────────

export function LayoutMain() {
  const { isOnline, pendingSync } = useOnlineStatus()
  const { cajero, logout } = useAuth()
  const { can } = usePermissions()
  const { canView: canViewWarranties } = useWarrantyPermissions()
  const { allowed: canVerifyTransfers } = useTransferVerifyAccess()
  const location = useLocation()
  const [showMore, setShowMore] = useState(false)
  // P0-1 · Fase 8G — aviso de logout con intenciones offline pendientes.
  const [logoutGuard, setLogoutGuard] = useState<number | null>(null)

  const requestLogout = async () => {
    const sales = getStoreConfig(cajero?.storeConfig ?? {}).sales
    const uid = cajero?.id
    const sid = cajero?.activeStoreId ?? ''
    const bid = cajero?.activeBusinessId ?? cajero?.businessId ?? ''
    if (sales.offline_intents_enabled && uid && sid && bid) {
      const { count } = await countPendingForGuard({
        user_id: uid,
        store_id: sid,
        business_id: bid,
      })
      if (count > 0) {
        setLogoutGuard(count)
        return
      }
    }
    void logout()
  }

  // Mantiene storeConfig alineado con la tienda activa (POS/Caja usan reglas
  // de la tienda ACTUAL, no la de login). Ver useActiveStoreConfigSync.
  useActiveStoreConfigSync()
  useMarcaTicketSync()

  // Config de la tienda ACTIVA (fresca vía useActiveStoreConfigSync).
  const storeConfig = getStoreConfig(cajero?.storeConfig ?? {})

  // Visible si: no exige permiso (Inicio) · o tiene el permiso · o (array) tiene
  // cualquiera de ellos. Mantiene el gating legacy actual, sin cambiar rutas.
  // Si el item define storeGate, además debe estar habilitado en la tienda.
  const isVisible = (i: NavItem): boolean => {
    // 00327: Garantías por permiso fino (warranties.view / owner), no por legacy.
    if (i.to === '/garantias') return canViewWarranties
    const permOk =
      i.permission == null
        ? true
        : Array.isArray(i.permission)
          ? i.permission.some(p => can(p))
          : can(i.permission)
    if (!permOk) return false
    if (i.storeGate && !i.storeGate(storeConfig).allowed) return false
    return true
  }

  // Modo Corporativo: usuarios con scope global/business tienen una entrada de
  // supervisión ("Corporativo" → /corporate). NO cambia la tienda activa ni
  // habilita operación; es una vista aparte. Los usuarios operativos (scope
  // store) no la ven.
  const isCorporate = cajero ? isCorporateScope(cajero.accessScope) : false

  // Secciones con solo los items visibles. Una sección sin items no se renderiza.
  const visibleSections = SECTIONS.map(s => {
    const items = s.items.filter(isVisible)
    if (s.key === 'principal' && isCorporate) {
      items.push({ to: '/corporate', icon: '🌐', label: 'Corporativo' })
    }
    // Bloque A — Verificación de transferencias (contador). Gate autoritativo por
    // RBAC (payments.verify_transfer), no por el mapa estático de roles.
    if (s.key === 'administracion' && canVerifyTransfers) {
      items.push({ to: '/transfer-verifications', icon: '🏦', label: 'Transferencias' })
    }
    return { ...s, items }
  }).filter(s => s.items.length > 0)

  // Item activo: el PRIMERO cuya ruta coincide (evita doble resaltado cuando
  // dos items comparten ruta, p.ej. Compras/Proveedores → /purchases).
  const flatItems = visibleSections.flatMap(s => s.items)
  const activeItem =
    flatItems.find(i => location.pathname === i.to || location.pathname.startsWith(i.to + '/')) ??
    null

  // Bottom nav móvil: primarios (máx 4) + "Más" con el resto. Se rellena con
  // otros items visibles si el usuario no tiene alguno de los primarios.
  const primaryItems: NavItem[] = []
  for (const route of MOBILE_PRIMARY) {
    if (primaryItems.length >= 4) break
    const it = flatItems.find(i => i.to === route)
    if (it) primaryItems.push(it)
  }
  if (primaryItems.length < 4) {
    for (const it of flatItems) {
      if (primaryItems.length >= 4) break
      if (!primaryItems.includes(it)) primaryItems.push(it)
    }
  }
  const moreItems = flatItems.filter(i => !primaryItems.includes(i))

  useEffect(() => {
    if (!cajero) return
    console.log('[REAL USER CONTEXT]', {
      email: cajero.email,
      name: cajero.name,
      role: cajero.role,
      accessScope: cajero.accessScope,
      accessibleBusinesses: cajero.accessibleBusinesses?.length ?? 0,
      accessibleStores: cajero.accessibleStores?.length ?? 0,
      activeBusinessId: cajero.activeBusinessId,
      activeBusinessBrand: cajero.activeBusinessBrand,
      activeStoreId: cajero.activeStoreId,
      activeStoreCode: cajero.activeStoreCode,
      storeId: cajero.storeId,
      storeCode: cajero.storeCode,
    })
  }, [cajero])

  // Marca activa → tema (data-brand). Aditivo: solo expone CSS variables de marca;
  // no cambia nada visual hasta que un componente use tokens brand-primary/etc.
  const brandTheme = resolveBrandTheme(cajero?.activeBusinessBrand ?? cajero?.businessBrand)

  return (
    <div data-brand={brandTheme} className="flex flex-col h-dvh bg-surface-50 overflow-hidden">
      {logoutGuard != null && (
        <OfflinePendingGuardModal
          count={logoutGuard}
          storeLabel={cajero?.activeStoreCode ?? cajero?.storeCode ?? '—'}
          title="Operaciones offline pendientes"
          message="Tienes operaciones offline pendientes. Si cierras sesión, la cola permanece en este dispositivo y solo tú podrás sincronizarla."
          confirmLabel="Cerrar sesión"
          onReview={() => {
            setLogoutGuard(null)
            window.location.assign('/pos/pendientes')
          }}
          onConfirm={() => {
            setLogoutGuard(null)
            void logout()
          }}
        />
      )}
      {/* ── TOPBAR ─────────────────────────────────────────── */}
      <header className="h-14 flex-shrink-0 bg-navy-900 text-white flex items-center gap-2 px-4 z-nav">
        <span className="font-black text-sm tracking-tight leading-none">
          {cajero?.activeBusinessBrand ?? cajero?.businessBrand ?? 'CELMAXX'}
        </span>

        <span className="text-navy-700 text-xs">·</span>

        {/* Indicador online/offline */}
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'w-2 h-2 rounded-full flex-shrink-0 transition-colors',
              isOnline ? 'bg-success-500' : 'bg-warning-500 animate-pulse'
            )}
          />
          <span
            className={cn(
              'text-xs font-medium',
              isOnline ? 'text-success-400' : 'text-warning-400'
            )}
          >
            {isOnline
              ? pendingSync > 0
                ? `${pendingSync} pendientes`
                : 'En línea'
              : 'Sin conexión'}
          </span>
        </div>

        <div className="flex-1" />

        {/* Centro de notificaciones (N1) */}
        {cajero && <NotificationBell />}

        {/* Selector de contexto activo (empresa · tienda) */}
        <ActiveContextSelector />

        {cajero && <span className="text-navy-700 text-xs">·</span>}

        {/* Cajero activo (rol + nombre + logout) */}
        {cajero && (
          <button
            onClick={() => void requestLogout()}
            className="flex items-center gap-1.5 text-xs text-surface-400 hover:text-white transition-colors"
            title={`Cerrar sesión — ${cajero.email} (${cajero.role})`}
          >
            <span className="text-[9px] font-bold uppercase tracking-wide bg-navy-700 text-navy-300 px-1.5 py-0.5 rounded">
              {cajero.role}
            </span>
            <span className="font-medium max-w-[80px] truncate">{cajero.name}</span>
          </button>
        )}
      </header>

      {/* ── SIDEBAR (md+) + CONTENT ────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar: rail de iconos en tablet (md), icono+label en desktop (lg+). */}
        <aside className="hidden md:flex md:flex-col md:w-16 lg:w-56 flex-shrink-0 bg-white border-r border-surface-200 overflow-y-auto no-scrollbar py-3 z-nav">
          {visibleSections.map(section => (
            <div key={section.key} className="mb-1">
              <p className="hidden lg:block px-4 pt-2 pb-1 text-[10px] font-bold text-surface-400 uppercase tracking-wide">
                {section.label}
              </p>
              {section.items.map(item => {
                const isActive = item === activeItem
                return (
                  <NavLink
                    key={`${section.key}-${item.label}`}
                    to={item.to}
                    aria-label={`${section.label}: ${item.label}`}
                    title={item.label}
                    className={cn(
                      'flex items-center gap-3 mx-2 px-2 lg:px-3 py-2 rounded-xl transition-all justify-center lg:justify-start',
                      isActive
                        ? 'bg-brand-primary-deep/10 text-brand-primary-deep font-bold'
                        : 'text-surface-500 hover:bg-surface-100 hover:text-surface-700'
                    )}
                  >
                    <NavIcon to={item.to} className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden lg:block text-[13px] truncate">{item.label}</span>
                  </NavLink>
                )
              })}
            </div>
          ))}
        </aside>

        {/* ── CONTENT ──────────────────────────────────────────
            overflow-y-auto: las páginas h-full se ajustan exactas (scroll
            interno propio, sin cambio); las que crecen (min-h-full) hacen
            scroll aquí en vez de recortarse. overflow-x-hidden evita
            desbordes horizontales. */}
        <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* ── BOTTOM NAV (solo móvil) ────────────────────────── */}
      <nav className="md:hidden h-[60px] flex-shrink-0 bg-white border-t border-surface-200 flex items-stretch z-nav">
        {primaryItems.map(item => {
          const isActive = item === activeItem
          return (
            <NavLink
              key={item.to}
              to={item.to}
              aria-label={item.label}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 transition-all focus:outline-none',
                isActive ? 'text-brand-primary-deep' : 'text-surface-400 hover:text-surface-600'
              )}
            >
              <NavIcon
                to={item.to}
                className={cn('w-5 h-5 transition-transform', isActive && 'scale-110')}
              />
              <span
                className={cn(
                  'text-[9px] font-semibold leading-none',
                  isActive ? 'text-brand-primary-deep' : 'text-surface-400'
                )}
              >
                {item.label}
              </span>
            </NavLink>
          )
        })}
        {moreItems.length > 0 && (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            aria-label="Más módulos"
            className="flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 text-surface-400 hover:text-surface-600 focus:outline-none"
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[9px] font-semibold leading-none">Más</span>
          </button>
        )}
      </nav>

      {/* ── SHEET "Más" (móvil) ────────────────────────────── */}
      {showMore && (
        <div
          className="md:hidden fixed inset-0 z-[200] flex items-end bg-black/40"
          onClick={e => {
            if (e.target === e.currentTarget) setShowMore(false)
          }}
        >
          <div className="w-full bg-white rounded-t-2xl max-h-[75dvh] overflow-y-auto p-4 pb-safe animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[16px] font-black text-navy-900">Más módulos</h2>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                aria-label="Cerrar"
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-100 text-surface-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {visibleSections.map(section => {
              const secItems = section.items.filter(i => moreItems.includes(i))
              if (secItems.length === 0) return null
              return (
                <div key={section.key} className="mb-3">
                  <p className="text-[10px] font-bold text-surface-400 uppercase tracking-wide mb-1.5">
                    {section.label}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {secItems.map(item => {
                      const isActive = item === activeItem
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setShowMore(false)}
                          className={cn(
                            'flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-all',
                            isActive
                              ? 'border-brand-primary-deep/30 bg-brand-primary-deep/10 text-brand-primary-deep'
                              : 'border-surface-200 text-surface-600 hover:bg-surface-50'
                          )}
                        >
                          <NavIcon to={item.to} className="w-5 h-5" />
                          <span className="text-[10px] font-semibold text-center leading-tight">
                            {item.label}
                          </span>
                        </NavLink>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
